/**
 * Pure rules for the operational-reminders screen, kept out of the component so
 * they are testable without a DOM.
 */
import type { OperationalReminder } from '../../services/reminders.service';

export type DueState = 'overdue' | 'due_soon' | 'scheduled';

/** The floor the server enforces on a finding. Mirrored here so the button is
 *  disabled before the round-trip rather than after it. */
export const MIN_FINDINGS = 20;

export function dueState(r: Pick<OperationalReminder, 'days_until_due' | 'lead_days'>): DueState {
  if (r.days_until_due < 0) return 'overdue';
  return r.days_until_due <= r.lead_days ? 'due_soon' : 'scheduled';
}

/**
 * When it is owed, in words. Says the number of days only while that number is
 * actionable: "in 8 months" is a diary entry and a countdown on it is noise.
 */
export function describeDue(r: Pick<OperationalReminder, 'days_until_due' | 'lead_days'>): string {
  const d = r.days_until_due;
  if (d < 0) return `${Math.abs(d)} day${Math.abs(d) === 1 ? '' : 's'} overdue`;
  if (d === 0) return 'Due today';
  if (d <= r.lead_days) return `Due in ${d} day${d === 1 ? '' : 's'}`;
  return 'Scheduled';
}

/** Overdue first, then soonest. What has lapsed is what a person must see. */
export function sortByUrgency(rows: OperationalReminder[]): OperationalReminder[] {
  return [...rows].sort((a, b) => a.days_until_due - b.days_until_due);
}

export function canSignOff(role: string | null): boolean {
  return role === 'super_admin';
}

export function isFindingReady(findings: string): boolean {
  return findings.trim().length >= MIN_FINDINGS;
}
